import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';

import AlertDialog from 'components/UI/AlertDialog';
import useCurrentUser from 'hooks/useCurrentUser';
import { format } from 'rut.js';
import { useCreateDemochatClientMutation, useGetIsPrivateDemoQuery } from 'services/api/demochat';
import getReCaptchaToken from 'utils/getReCaptchaToken';

import ChatWrapper from './ChatWrapper';

const BUSINESS_UNITS_WITH_FORM: string[] = [
  'salfa-nurturing',
  'pompeyo-chat',
  'auto-chat',
  'salfa-rent',
  'bruno-fritsch',
];
type BusinessUnitWithForm = (typeof BUSINESS_UNITS_WITH_FORM)[number];

const sourcesByBusinessUnit: Record<BusinessUnitWithForm, string[]> = {
  'bruno-fritsch': ['ZENDESK: api', 'ZENDESK: Toyota importador', 'BRUNO_FRITSCH_API: rechazados'],
};

const brandsByBusinessUnit: Record<BusinessUnitWithForm, string[]> = {
  'bruno-fritsch': ['USADOS', 'TOYOTA'],
  'salfa-nurturing': ['JEEP', 'CHERY', 'RAM', 'MITSUBISHI', 'SSANGYONG', 'FIAT', 'JMC', 'GAC', 'EXEED', 'BYD'],
  'pompeyo-chat': ['OPEL', 'KIA', 'NISSAN', 'PEUGEOT', 'MG', 'GEELY', 'DFSK', 'SUBARU', 'CITROËN'],
  'auto-chat': ['OPEL', 'KIA', 'NISSAN', 'PEUGEOT', 'MG', 'GEELY', 'DFSK', 'SUBARU', 'CITROËN'],
  'salfa-rent': ['USADOS'],
};

const mapAttributes = (key: string) => {
  if (key === 'rut') {
    return 'Rut';
  }
  if (key === 'email') {
    return 'Email';
  }
  if (key === 'modelOfInterest') {
    return 'Modelo de Interés';
  }
  if (key === 'firstName') {
    return 'Nombre';
  }
  if (key === 'lastName') {
    return 'Apellido';
  }
  return key;
};

const NewDemochat: React.FC = () => {
  const { companyIdentifier } = useParams<{ companyIdentifier: string }>();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { data: privateDemoData } = useGetIsPrivateDemoQuery({
    companyIdentifier: companyIdentifier ?? '',
  });
  if (privateDemoData && privateDemoData.isPrivateDemo) {
    if (!currentUser || currentUser.id === 0) {
      navigate(`/sign-in?redirect=/demochat/${companyIdentifier}`);
    }
  }

  let sources: string[] = [];
  let brands: string[] = [];
  if (companyIdentifier && BUSINESS_UNITS_WITH_FORM.includes(companyIdentifier)) {
    brands = brandsByBusinessUnit[companyIdentifier] || [];
    sources = sourcesByBusinessUnit[companyIdentifier] || [];
  }

  const [openForm, setOpenForm] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [clientData, setClientData] = useState({
    firstName: '',
    lastName: '',
    rut: '',
    email: '',
    modelOfInterest: '',
    brandOfInterest: brands.length > 0 ? brands[0] : '',
    source: sources.length > 0 ? sources[0] : '',
  });

  const localStorageKey = `zeller-pusher-demo-${companyIdentifier}`;
  const clientPhoneIdentifier = localStorage.getItem(localStorageKey);

  const [createDemochatClient, { data: createClientData, error }] = useCreateDemochatClientMutation();
  const [searchParams] = useSearchParams();

  if (!companyIdentifier) {
    navigate('/demochat'); // Error page
    return null;
  }

  const startChat = () => {
    getReCaptchaToken()
      .then((recaptchaToken) => {
        createDemochatClient({ companyIdentifier, recaptchaToken, clientData }).catch((e) => {
          // eslint-disable-next-line no-console
          console.error('Error creating demo chat client', e);
          setOpenError(true);
        });
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('Error getting recaptcha token', e);
        setOpenError(true);
      });
  };

  const navigateToChat = () => {
    const messageParam = searchParams.get('message');
    const autoStartChatParam = searchParams.get('autoStartChat');
    let url = `/demochat/${companyIdentifier}`;
    if (messageParam || autoStartChatParam) {
      url += '?';
    }
    if (messageParam) {
      url += `message=${encodeURIComponent(messageParam)}&`;
    }
    if (autoStartChatParam) {
      url += `autoStartChat=${autoStartChatParam}`;
    }
    navigate(url);
  };

  useEffect(() => {
    if (createClientData) {
      localStorage.setItem(localStorageKey, createClientData.id);
      navigateToChat();
    }
  }, [createClientData]);

  useEffect(() => {
    if (error) {
      if ('sourcealStatus' in error && error.sourcealStatus === 401) {
        setErrorMessage('No tienes permisos para iniciar un chat');
      } else {
        setErrorMessage('Ha ocurrido un error iniciando el chat');
      }
      setOpenError(true);
    }
  }, [error]);

  // If it has the autoStartChat param, then start the chat automatically
  useEffect(() => {
    const autoStartChatParam = searchParams.get('autoStartChat');
    if (autoStartChatParam) {
      // sleep for half a second so that the recaptcha is loaded
      setTimeout(() => {
        startChat();
      }, 500);
    }
  }, []);

  return (
    <ChatWrapper companyIdentifier={companyIdentifier}>
      <>
        <AlertDialog
          openDialog={openError}
          setOpenDialog={setOpenError}
          header={errorMessage}
          msg="Intenta nuevamente, con otros datos. O inicia sesión con otro usuario."
        />
        {openForm ? (
          <Stack spacing={1} pt={2} m={6}>
            {Object.entries(clientData)
              .filter(([k]) => !k.includes('brand') && k !== 'source')
              .map(([key, value]) => (
                <TextField
                  key={key}
                  label={mapAttributes(key)}
                  value={value}
                  onChange={(e) =>
                    setClientData({ ...clientData, [key]: key === 'rut' ? format(e.target.value) : e.target.value })
                  }
                  sx={{ backgroundColor: 'white' }}
                  fullWidth
                />
              ))}
            {sources && sources.length > 0 && (
              <FormControl>
                <InputLabel>Origen</InputLabel>
                <Select
                  value={clientData.source}
                  label="Origen"
                  sx={{ backgroundColor: 'white' }}
                  onChange={(e) => setClientData({ ...clientData, source: e.target.value })}
                >
                  {sources.map((source) => (
                    <MenuItem key={source} value={source}>
                      {source}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {brands && brands.length > 0 && (
              <FormControl>
                <InputLabel>Marca de interés</InputLabel>
                <Select
                  value={clientData.brandOfInterest}
                  label="Marca"
                  onChange={(e) => setClientData({ ...clientData, brandOfInterest: e.target.value })}
                  sx={{ backgroundColor: 'white' }}
                >
                  {brands.map((brand) => (
                    <MenuItem key={brand} value={brand}>
                      {brand}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button variant="contained" color="primary" onClick={startChat}>
              Iniciar Chat
            </Button>
          </Stack>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-evenly',
              height: '100%',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Button variant="contained" color="primary" onClick={startChat} autoFocus>
              Iniciar Chat
            </Button>
            {BUSINESS_UNITS_WITH_FORM.includes(companyIdentifier) && (
              <Button variant="contained" color="primary" onClick={() => setOpenForm(true)}>
                Iniciar Chat con Datos
              </Button>
            )}

            {clientPhoneIdentifier && (
              <Button variant="contained" color="primary" onClick={navigateToChat}>
                Continuar chat
              </Button>
            )}
          </Box>
        )}
      </>
    </ChatWrapper>
  );
};

export default NewDemochat;
